import { returnRetrievalRankingDebugInfo } from "@faire/retailer-shared/search/loggedInBrandLeanProductTilesRequest";
import { getSettingShouldDisplayRetrievalAndRankingDebugInfo } from "@faire/retailer-visitor-shared/settings/getSettingShouldDisplayRetrievalAndRankingDebugInfo";
import { UserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import {
  cachingApi,
  cachingApiWithLookup,
} from "@faire/web--source/common/cachingApi";
import { singletonGetter } from "@faire/web--source/common/singletons/getter";
import { createStoreHook } from "@faire/web--source/ui/hooks/useStore";
import { path as getPathPublishedBrandProductCollectionMetadata } from "@faire/web-api--source/api/brand/brandToken/published-product-collection-metadata/get";
import fetchCategoryPageLayoutElements from "@faire/web-api--source/api/layout/category-page/post";
import brandSearch, {
  QueryParameters as BrandSearchParameters,
} from "@faire/web-api--source/api/v2/search/brands/get";
import getFeaturedProducts, {
  QueryParameters as FeaturedProductsParameters,
} from "@faire/web-api--source/api/v2/search/brandToken/featured-products/get";
import fetchProductCatNav from "@faire/web-api--source/api/v2/search/products/cat-nav/post";
import { path as getPathProductsFromBrandFilters } from "@faire/web-api--source/api/v2/search/products/from-brand/filters/post";
import searchProductsFromBrand, {
  path as getPathProductsFromBrand,
} from "@faire/web-api--source/api/v2/search/products/from-brand/post";
import queryProductsFilterService from "@faire/web-api--source/api/v3/search/products/filters/post";
import queryProductsSearchService from "@faire/web-api--source/api/v3/search/products/post";
import { IGetProductFilterSectionInfoResponse } from "@faire/web-api--source/indigofair/data/IGetProductFilterSectionInfoResponse";
import { IGetSearchProductsInfoResponse } from "@faire/web-api--source/indigofair/data/IGetSearchProductsInfoResponse";
import { IProductFirstCatNavRequest } from "@faire/web-api--source/indigofair/data/IProductFirstCatNavRequest";
import { IProductFirstCatNavWithLeanProductTileResponse } from "@faire/web-api--source/indigofair/data/IProductFirstCatNavWithLeanProductTileResponse";
import { IQueryProductsRequest } from "@faire/web-api--source/indigofair/data/IQueryProductsRequest";
import { ISearchBrandsV2Response } from "@faire/web-api--source/indigofair/data/ISearchBrandsV2Response";
import { ISearchProductsFromBrandRequest } from "@faire/web-api--source/indigofair/data/ISearchProductsFromBrandRequest";
import { EndpointOptions } from "@faire/web-api--source/types";
import type { QueryClient } from "@tanstack/react-query";

const queryProductsWrapper = (
  data: IQueryProductsRequest,
  abortSignal?: AbortSignal
): Promise<IGetSearchProductsInfoResponse> => {
  return queryProductsSearchService(data, { signal: abortSignal });
};

const queryFiltersWrapper = (
  data: IQueryProductsRequest,
  abortSignal?: AbortSignal
): Promise<IGetProductFilterSectionInfoResponse> => {
  return queryProductsFilterService(data, { signal: abortSignal });
};

const fetchProductCatNavWrapper = (
  data: IProductFirstCatNavRequest,
  requestOptions?: EndpointOptions
): Promise<IProductFirstCatNavWithLeanProductTileResponse> => {
  data.return_retrieval_and_ranking_debug_info =
    returnRetrievalRankingDebugInfo(
      UserStore.get().user,
      getSettingShouldDisplayRetrievalAndRankingDebugInfo()
    );
  // New search service API that searches the products.
  return fetchProductCatNav(data, requestOptions);
};

// need this for tests
const brandSearchWrapper = (
  payload: BrandSearchParameters,
  requestOptions?: EndpointOptions
): Promise<ISearchBrandsV2Response> => brandSearch(payload, requestOptions);

export class ProductCacheStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(ProductCacheStore);

  fetchProductCatNav = cachingApi(fetchProductCatNavWrapper);

  fetchCategoryPageLayoutElements = cachingApi(fetchCategoryPageLayoutElements);

  // searchProductsFromBrandCache needs wrapper to enable manual cache invalidation
  // when a product's state is modified
  private searchProductsFromBrandCache = cachingApiWithLookup(
    (request: ISearchProductsFromBrandRequest) => {
      if (!request.brand_token) {
        return Promise.reject("Empty brand_token");
      }
      return searchProductsFromBrand(request);
    }
  );

  private queryProductsMemoized = cachingApi(queryProductsWrapper);

  private queryFiltersMemoized = cachingApi(queryFiltersWrapper);

  private brandSearchMemoized = cachingApi(brandSearchWrapper);

  private getFeaturedLeanProductTilesMemoized = cachingApi(getFeaturedProducts);

  searchProductsFromBrand = (payload: ISearchProductsFromBrandRequest) => {
    this.searchProductsFromBrandCache.lookup.add(payload);
    return this.searchProductsFromBrandCache.cache(payload);
  };

  // Search results view caches

  queryProducts = (
    payload: IQueryProductsRequest,
    abortSignal?: AbortSignal
  ) => {
    return this.queryProductsMemoized(payload, abortSignal);
  };

  queryFilters = (
    payload: IQueryProductsRequest,
    abortSignal?: AbortSignal
  ) => {
    return this.queryFiltersMemoized(payload, abortSignal);
  };

  // Category view results view caches
  brandSearch = (
    payload: BrandSearchParameters,
    requestOptions?: EndpointOptions
  ) => {
    return this.brandSearchMemoized(payload, requestOptions);
  };

  getFeaturedLeanProductTiles = (
    brandToken: string,
    queryParams?: FeaturedProductsParameters
  ) => {
    return this.getFeaturedLeanProductTilesMemoized(brandToken, queryParams);
  };

  // Invalidate a brand's product cache when products of that
  // brand have had their state modified
  invalidateCache = (
    brandToken: string | undefined,
    queryClient: QueryClient | null
  ) => {
    if (brandToken === undefined) {
      return;
    }

    // Invalidate the entire cache of search calls if any product is favorited
    this.queryProductsMemoized.clear();
    this.queryFiltersMemoized.clear();
    this.brandSearchMemoized.clear();
    this.getFeaturedLeanProductTilesMemoized.clear();

    this.searchProductsFromBrandCache.lookup.forEach((payload) => {
      if (payload.brand_token === brandToken) {
        this.searchProductsFromBrandCache.cache.delete(payload);
      }
    });

    if (queryClient) {
      queryClient.removeQueries({
        queryKey: [getPathPublishedBrandProductCollectionMetadata(brandToken)],
      });
      queryClient.removeQueries({
        queryKey: [getPathProductsFromBrand(), { brand_token: brandToken }],
      });
      queryClient.removeQueries({
        queryKey: [
          getPathProductsFromBrandFilters(),
          { brand_token: brandToken },
        ],
      });
    }
  };

  invalidateCacheAfterPlacingAndCancelingOrders = () => {
    this.brandSearchMemoized.clear();
    this.queryProductsMemoized.clear();
    this.queryFiltersMemoized.clear();
  };

  invalidateCacheAfterInsiderEnrollment = () => {
    this.brandSearchMemoized.clear();
  };
}

export const useProductCacheStore = createStoreHook(ProductCacheStore);
